import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useReducer,
} from "react";
import { ReactSvgPanZoomLoader } from "../utils/ReactSvgPanZoomLoader.js";
import MapEffectComponent from "./MapEffectComponent.js";
import Button from "@material-ui/core/Button";
import MapDialog from "./dialog/MapDialog";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import CircularProgress from "@material-ui/core/CircularProgress";
import campusData from "../../json/campus_data.json";
import { USE_GPS } from "../../config/gpsConfig.js";
import { NORTH_INDICATOR_DEFAULT } from "../../config/northIndicatorConfig.js";
import { loadMapData } from "../utils/map/loadMapData.js";
import MapToolbar from "./MapToolbar.jsx";
import SearchPin from "./marker/SearchPin.jsx";
import GpsMarker from "./marker/GpsMarker.jsx";
import NearestCampusSnackbar from "./snackbar/NearestCampusSnackbar.jsx";
import IconGuideSnackbar from "./snackbar/IconGuideSnackbar.jsx";
import { useLanguage, LANG } from "../../contexts/LanguageContext";
import { createSearchQueryController } from "../utils/searchQueryController.js";
import {
  initGpsOnMount,
  cleanupGps,
  initGpsOnSvgReady,
  centerOnCurrentGps,
  createGpsStore,
} from "./managers/gpsManager.js";
import {
  ensureIndoorLayerContainer,
  stashFloors,
  changeFloor,
  createFloorStore,
} from "./managers/floorManager.js";
import { handleSelect, handleClick } from "./managers/selectionHandler.js";
import {
  handleCampusChange,
  initializeMap,
  clearSearchPin,
} from "./managers/campusManager.js";
import {
  searchPinReducer,
  searchPinInitialState,
  gpsReducer,
  gpsInitialState,
  mapReducer,
  createMapInitialState,
  uiReducer,
  uiInitialState,
  gpsActions,
  mapActions,
  uiActions,
  MapModeEnum,
} from "./reducers/mapPanZoomReducer.js";

const getMapSvgSrc = (mapSvgEntry, language) => {
  if (!mapSvgEntry) return null;
  if (typeof mapSvgEntry === "string") return mapSvgEntry;
  if (language === LANG.EN && mapSvgEntry.en) return mapSvgEntry.en;
  return mapSvgEntry.default;
};

export default function MapPanZoomWrapper(props) {
  const { width, height, initialCampus, gpsEnabled, updateGpsEnabled } = props;
  const { language } = useLanguage();

  // Reducers
  const [searchPin, searchPinDispatch] = useReducer(
    searchPinReducer,
    searchPinInitialState,
  );
  const [gpsState, gpsDispatch] = useReducer(gpsReducer, gpsInitialState);
  const [mapState, mapDispatch] = useReducer(
    mapReducer,
    initialCampus,
    createMapInitialState,
  );
  const [uiState, uiDispatch] = useReducer(uiReducer, uiInitialState);

  // Local state
  const [showNorthIndicator, setShowNorthIndicator] = useState(
    NORTH_INDICATOR_DEFAULT,
  );
  const [currentScale, setCurrentScale] = useState(1);
  const [mapData, setMapData] = useState({
    mapJson: [],
    mapIconJson: [],
    mapModelJson: [],
    mapSvg: [],
  });

  // Refs
  const transformRef = useRef(null);
  const viewerRef = useRef(null);
  const svgRootRef = useRef(null);
  const sizeRef = useRef({ width, height });
  sizeRef.current = { width, height };
  const gpsStoreRef = useRef(createGpsStore(mapState.currentCampus));
  const floorStoreRef = useRef(createFloorStore());
  const searchQueryControllerRef = useRef(null);
  const gpsAccuracyRef = useRef(null);
  const previousMapSvgSrcRef = useRef(null);
  const previousLanguageRef = useRef(language);

  // searchQueryController 内のコールバックが古い state を保持しないよう、最新 state を ref 経由で参照させるため
  const searchQueryStateRef = useRef({
    isReady: mapState.isReady,
    currentCampus: mapState.currentCampus,
    currentSearchArrayLength: mapState.currentSearchArray.length,
    currentBuildingArray: mapState.currentBuildingArray,
    currentMapJson: mapState.currentMapJson,
    currentFloorArray: mapState.currentFloorArray,
    currentRoomArray: mapState.currentRoomArray,
    currentMode: mapState.currentMode,
    mapIconJson: mapData.mapIconJson,
    searchPin,
  });
  searchQueryStateRef.current = {
    isReady: mapState.isReady,
    currentCampus: mapState.currentCampus,
    currentSearchArrayLength: mapState.currentSearchArray.length,
    currentBuildingArray: mapState.currentBuildingArray,
    currentMapJson: mapState.currentMapJson,
    currentFloorArray: mapState.currentFloorArray,
    currentRoomArray: mapState.currentRoomArray,
    currentMode: mapState.currentMode,
    mapIconJson: mapData.mapIconJson,
    searchPin,
  };

  gpsAccuracyRef.current = gpsState.coords?.accuracy ?? null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    viewerRef.current = {
      getValue: () => ({
        a: transformRef.current?.state?.scale ?? 1,
        e: transformRef.current?.state?.positionX ?? 0,
        f: transformRef.current?.state?.positionY ?? 0,
        viewerWidth: sizeRef.current.width,
        viewerHeight: sizeRef.current.height,
      }),
      setPointOnViewerCenter: (x, y, zoom) => {
        const posX = sizeRef.current.width / 2 - x * zoom;
        const posY = sizeRef.current.height / 2 - y * zoom;
        transformRef.current?.setTransform(posX, posY, zoom, 0);
      },
      fitToViewer: () => transformRef.current?.resetTransform(0),
      reset: () => transformRef.current?.resetTransform(0),
    };
  }, []);

  // コンポーネント内とmanager群で利用する統合dispatch（domainで各reducerに振り分け）
  const dispatch = useCallback(
    (action) => {
      switch (action.domain) {
        case "searchPin":
          searchPinDispatch(action);
          break;
        case "gps":
          gpsDispatch(action);
          break;
        case "map":
          mapDispatch(action);
          break;
        case "ui":
          uiDispatch(action);
          break;
        default:
          console.warn("Unknown action domain:", action.domain);
      }
    },
    [searchPinDispatch, gpsDispatch, mapDispatch, uiDispatch],
  );

  const currentMapSvgSrc = getMapSvgSrc(
    mapData.mapSvg[mapState.currentCampus - 1],
    language,
  );

  const updateSearchParamsFromSelection = useCallback((selection) => {
    searchQueryControllerRef.current?.updateSearchParamsFromSelection(
      selection,
    );
  }, []);

  // mapState.currentCampus を GPS/コンパス購読用 ref に同期
  useEffect(() => {
    gpsStoreRef.current.runtimeCampus = mapState.currentCampus;
  }, [mapState.currentCampus]);

  // ── searchQueryControllerの初期化 ──
  useEffect(() => {
    searchQueryControllerRef.current = createSearchQueryController({
      clearSearchPin: () => clearSearchPin(dispatch),
      handleCampusChange: (v) => {
        handleCampusChange(
          viewerRef,
          dispatch,
          updateSearchParamsFromSelection,
          v,
        );
      },
      handleSelect: (id) => {
        const mapSlice = {
          currentBuildingArray:
            searchQueryStateRef.current.currentBuildingArray,
          currentMapJson: searchQueryStateRef.current.currentMapJson,
          currentFloorArray: searchQueryStateRef.current.currentFloorArray,
          currentRoomArray: searchQueryStateRef.current.currentRoomArray,
          currentCampus: searchQueryStateRef.current.currentCampus,
          currentMode: searchQueryStateRef.current.currentMode,
        };

        handleSelect(
          floorStoreRef,
          svgRootRef,
          viewerRef,
          dispatch,
          mapSlice,
          searchQueryStateRef.current.mapIconJson,
          searchQueryStateRef.current.searchPin,
          id,
        );
      },
      getIsReady: () => searchQueryStateRef.current.isReady,
      getSvgRoot: () => svgRootRef.current,
      getCurrentCampus: () => searchQueryStateRef.current.currentCampus,
      getSearchArrayLength: () =>
        searchQueryStateRef.current.currentSearchArrayLength,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── isReadyがtrueになったらフロア初期化 ──
  useEffect(() => {
    if (mapState.isReady && svgRootRef.current) {
      ensureIndoorLayerContainer(svgRootRef.current);
      stashFloors(
        floorStoreRef.current,
        mapState.currentCampus,
        mapData.mapJson,
      );
      initGpsOnSvgReady(
        gpsStoreRef,
        svgRootRef,
        dispatch,
        mapState.currentCampus,
        gpsAccuracyRef.current,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState.isReady, mapState.currentCampus, mapData.mapJson]);

  // ── マウント時のGPS初期化 ──
  useEffect(() => {
    const gpsStore = gpsStoreRef.current;

    if (viewerRef.current) viewerRef.current.fitToViewer();

    initGpsOnMount(gpsStoreRef, svgRootRef, dispatch, {
      gpsEnabled,
      updateGpsEnabled,
      initialCampus,
    });

    return () => {
      cleanupGps(gpsStore);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCampus, gpsEnabled, updateGpsEnabled]);

  // ── IconGuide Snackbar の表示 ──
  useEffect(() => {
    const showIconGuide =
      process.env.REACT_APP_SHOW_ICON_GUIDE_SNACKBAR === "true";
    if (showIconGuide) {
      dispatch(uiActions.setIconGuideSnackbar(true));
    }
  }, [dispatch]);

  // ── isReady・GPS可視状態変更時の処理 ──
  useEffect(() => {
    searchQueryControllerRef.current?.queueSearchFromQuery();
    searchQueryControllerRef.current?.tryHandlePendingSearchSelection();

    if (
      gpsStoreRef.current.pendingCenterOnGps &&
      mapState.isReady &&
      gpsState.markerVisible
    ) {
      gpsStoreRef.current.pendingCenterOnGps = false;
      const gpsSlice = {
        nearestCampus: gpsState.nearestCampus,
        markerVisible: gpsState.markerVisible,
        markerX: gpsState.markerX,
        markerY: gpsState.markerY,
      };
      const mapSlice = {
        currentCampus: mapState.currentCampus,
        currentCampusCode: mapState.currentCampusCode,
      };

      centerOnCurrentGps(
        gpsStoreRef,
        viewerRef,
        svgRootRef,
        gpsSlice,
        mapSlice,
        {
          gpsEnabled,
          handleCampusChange: (v) => {
            handleCampusChange(
              viewerRef,
              dispatch,
              updateSearchParamsFromSelection,
              v,
            );
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapState.isReady,
    gpsState.markerVisible,
    gpsState.nearestCampus,
    gpsState.markerX,
    gpsState.markerY,
    mapState.currentCampus,
    mapState.currentCampusCode,
    gpsEnabled,
  ]);

  // ── マップデータのロード ──
  useEffect(() => {
    if (mapData.mapSvg.length === 0) {
      loadMapData().then((data) => {
        setMapData({
          mapSvg: data.mapSvg,
          mapJson: data.mapJson,
          mapIconJson: data.mapIconJson,
          mapModelJson: data.mapModelJson,
        });
      });
    }
  }, [mapData.mapSvg.length]);

  // ── SVGソース変更 ──
  useEffect(() => {
    if (!currentMapSvgSrc) return;
    if (previousMapSvgSrcRef.current === currentMapSvgSrc) return;

    previousMapSvgSrcRef.current = currentMapSvgSrc;
    svgRootRef.current = null;
    floorStoreRef.current = createFloorStore();
    dispatch(mapActions.setReady(false));
  }, [currentMapSvgSrc, dispatch]);

  // ── 言語変更時の検索状態リセット ──
  useEffect(() => {
    if (previousLanguageRef.current === language) return;
    previousLanguageRef.current = language;
    searchQueryControllerRef.current?.clearSearchTarget(mapState.currentCampus);
  }, [language, mapState.currentCampus]);

  // ── キャンパス変更 ──
  const handleCampusChangeCallback = useCallback((campusValue) => {
    handleCampusChange(
      viewerRef,
      dispatch,
      updateSearchParamsFromSelection,
      campusValue,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── クリックハンドラ ──
  const handleClickCallback = useCallback(
    (target_id) => {
      const mapSlice = {
        currentBuildingArray: mapState.currentBuildingArray,
        currentMapJson: mapState.currentMapJson,
        currentCampus: mapState.currentCampus,
      };

      handleClick(
        floorStoreRef,
        svgRootRef,
        dispatch,
        mapSlice,
        mapData.mapIconJson,
        searchPin,
        target_id,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mapState.currentBuildingArray,
      mapState.currentMapJson,
      mapState.currentCampus,
      mapData.mapIconJson,
      searchPin,
    ],
  );

  // ── マップ初期化 ──
  const handleInitializeMap = useCallback(() => {
    initializeMap(
      viewerRef,
      svgRootRef,
      floorStoreRef,
      dispatch,
      mapState.currentCampus,
      mapData.mapJson,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState.currentCampus, mapData.mapJson]);

  // ── 視点リセット（パン/ズームのみ） ──
  const handleResetView = useCallback(() => {
    viewerRef.current?.reset?.();
  }, []);

  // ── フロア変更 ──
  const handleChangeFloor = useCallback(
    (index) => {
      const mapSlice = {
        currentCampus: mapState.currentCampus,
        currentSelectedFloors: mapState.currentSelectedFloors,
      };

      changeFloor(
        floorStoreRef,
        svgRootRef,
        dispatch,
        mapSlice,
        searchPin,
        index,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapState.currentCampus, mapState.currentSelectedFloors, searchPin],
  );

  // ── GPS中央フォーカス ──
  const handleCenterOnCurrentGps = useCallback(() => {
    const gpsSlice = {
      nearestCampus: gpsState.nearestCampus,
      markerVisible: gpsState.markerVisible,
      markerX: gpsState.markerX,
      markerY: gpsState.markerY,
    };
    const mapSlice = {
      currentCampus: mapState.currentCampus,
      currentCampusCode: mapState.currentCampusCode,
    };

    centerOnCurrentGps(gpsStoreRef, viewerRef, svgRootRef, gpsSlice, mapSlice, {
      gpsEnabled,
      handleCampusChange: (v) => {
        handleCampusChange(
          viewerRef,
          dispatch,
          updateSearchParamsFromSelection,
          v,
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gpsEnabled,
    gpsState.nearestCampus,
    gpsState.markerVisible,
    gpsState.markerX,
    gpsState.markerY,
    mapState.currentCampus,
    mapState.currentCampusCode,
  ]);

  // ── Snackbar ──
  const handleCloseNearestCampusSnackbar = useCallback(() => {
    dispatch(gpsActions.dismissSnackbar());
  }, [dispatch]);

  const handleCloseIconGuideSnackbar = useCallback(() => {
    dispatch(uiActions.setIconGuideSnackbar(false));
  }, [dispatch]);

  const handleConfirmNearestCampus = useCallback(() => {
    if (
      gpsState.nearestCampus &&
      gpsState.nearestCampus !== mapState.currentCampus
    ) {
      handleCampusChange(
        viewerRef,
        dispatch,
        updateSearchParamsFromSelection,
        gpsState.nearestCampus,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsState.nearestCampus, mapState.currentCampus]);

  // ── 検索 ──
  const handleSearchSelection = useCallback((val, reason) => {
    searchQueryControllerRef.current?.handleSearchSelection(val, reason);
  }, []);

  // ── SVG Ready ──
  const handleSvgReady = useCallback(
    (svgRoot) => {
      if (!svgRoot) return;
      svgRootRef.current = svgRoot;

      gpsStoreRef.current.transformModels[mapState.currentCampus] =
        mapData.mapModelJson[mapState.currentCampus - 1];
      gpsStoreRef.current.prevSvgPos[mapState.currentCampus] = null;

      dispatch(mapActions.setReady(true));
    },
    [dispatch, mapState.currentCampus, mapData.mapModelJson],
  );

  const indoorChanger =
    mapState.currentMode === MapModeEnum.INDOOR ? (
      <ButtonGroup
        orientation="vertical"
        color="secondary"
        size="small"
        aria-label="vertical contained primary button group "
        variant="contained"
      >
        {mapState.currentSelectedFloors.map((floorValue, index) => (
          <Button
            color={
              mapState.currentSelectedFloorIndex === index ? "primary" : null
            }
            onClick={() => handleChangeFloor(index)}
            key={index}
          >
            {floorValue.floor_name}
          </Button>
        ))}
      </ButtonGroup>
    ) : null;

  if (mapData.mapSvg.length === 0 || !currentMapSvgSrc) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#F4F4F4",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <MapDialog
        onClose={() => dispatch(uiActions.closeDialog())}
        open={uiState.dialogIsOpen}
        marker={uiState.selectedMarker}
      />
      <MapEffectComponent
        isReady={mapState.isReady}
        initializeMap={handleInitializeMap}
      />
      <ReactSvgPanZoomLoader
        key={currentMapSvgSrc}
        src={currentMapSvgSrc}
        onSVGReady={handleSvgReady}
        render={(content) => (
          <TransformWrapper
            ref={transformRef}
            initialScale={1}
            minScale={1}
            maxScale={20}
            limitToBounds={false}
            doubleClick={{ disabled: true }}
            wheel={{ step: 0.2, smoothStep: 0.03 }}
            velocityAnimation={{ disabled: true }}
            alignmentAnimation={{ disabled: true }}
            zoomAnimation={{
              disabled: true,
            }}
            onTransformed={(_, state) => setCurrentScale(state.scale)}
          >
            <TransformComponent
              wrapperStyle={{
                width: width,
                height: height,
                background: "#F4F4F4",
                touchAction: "none",
              }}
              contentStyle={{
                width: width,
                height: height,
              }}
            >
              <svg
                width={width}
                height={height}
                onClick={(e) => handleClickCallback(e.target.id)}
              >
                {content}
              </svg>
            </TransformComponent>
          </TransformWrapper>
        )}
      />
      <MapToolbar
        handleClick={handleSearchSelection}
        handleCenterOnGps={handleCenterOnCurrentGps}
        handleResetView={handleResetView}
        useGps={USE_GPS}
        isGpsAvailable={gpsEnabled !== "denied" && gpsEnabled !== "retry"}
        currentCampus={mapState.currentCampus}
        changeCampus={handleCampusChangeCallback}
        listArray={mapState.currentSearchArray}
        northIndicatorChecked={showNorthIndicator}
        onNorthIndicatorChange={setShowNorthIndicator}
      >
        {indoorChanger}
      </MapToolbar>
      <SearchPin
        svgRoot={svgRootRef.current}
        x={searchPin.x}
        y={searchPin.y}
        visible={searchPin.visible}
        zoom={currentScale}
        scale={
          campusData.campusList[mapState.currentCampus - 1].map?.searchPin
            ?.scale ?? 1
        }
      />
      <GpsMarker
        svgRoot={svgRootRef.current}
        x={gpsState.markerX}
        y={gpsState.markerY}
        visible={gpsState.markerVisible}
        zoom={currentScale}
        accuracy={gpsState.coords?.accuracy}
        accuracyRadius={gpsState.accuracyRadius}
        headingDeg={gpsState.headingDeg}
        scale={
          campusData.campusList[mapState.currentCampus - 1].map?.gpsMarker
            ?.scale ?? 1
        }
        northOffsetDeg={
          campusData.campusList[mapState.currentCampus - 1].map
            ?.northOffsetDeg ?? 0
        }
        showNorthIndicator={showNorthIndicator}
      />
      <NearestCampusSnackbar
        open={gpsState.showNearestCampusSnackbar}
        onClose={handleCloseNearestCampusSnackbar}
        onConfirm={handleConfirmNearestCampus}
        nearestCampus={gpsState.nearestCampus}
      />
      <IconGuideSnackbar
        open={uiState.showIconGuideSnackbar}
        onClose={handleCloseIconGuideSnackbar}
      />
    </>
  );
}
